<template>
  <div class="reports-of-problems-uk graphic-container" :class="{ 'graphic-container--social-mode': this.socialMode }">
    <h3 class="chart-hed" v-if="showHeading">
      Malfunction and injury reports associated with medical devices in the UK
      have doubled since 2008, but the number investigated by the UK regulator has fallen
    </h3>
    <ul class="chart-legend">
      <li v-for="(series, i) in seriesNames" :key="series">
        <span class="chart-legend__box" :style="{'background-color': colorScale(series)}"></span>
        {{ labels[i] }}
      </li>
    </ul>
    <svg :width="width" :height="height" class="line-chart--multi-series">
      <g id="reports-of-problems-uk__x-axis" class="axis axis--x"></g>
      <g id="reports-of-problems-uk__y-axis" class="axis axis--y"></g>
      <g id="reports-of-problems-uk" :style="{transform: `translate(${margin.left}px, ${margin.top}px)`}">
        <path v-for="(line, i) in lines" :key="i" class="line" :d="line.line" :stroke="line.color" />
      </g>
    </svg>
    <p class="graphic-source">
      Source: Medicines and Healthcare products Regulatory Agency (MHRA)
    </p>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import * as d3 from 'd3';
import data from '../assets/reports-of-problems-involving-medical-devices-since-2008-in-uk.json'
import embeddable from '../mixins/embeddable'

const props = {
  margin: {
    type: Object,
    default: () => ({
      left: 50,
      right: 20,
      top: 30,
      bottom: 40,
    }),
  },
  seriesNames: {
    type: Array,
    default: () => ([
      'incidentReports',
      'specialistInvestigationsByRegulator'
    ]),
  }
};

export default {
  name: 'HealthcareIndustryChart',
  mixins: [embeddable],
  props,
  data() {
    return {
      data,
      width: 0,
      height: 0,
      lines: [],
      scale: {
        x: null,
        y: null,
      },
      points: [],
      labels: ['Incident Reports', 'Specialist Investigations by regulator'],
      colorScale: d3.scaleOrdinal()
        .domain(this.seriesNames)
        .range(['#A83324', '#F8B700']),
    }
  },
  computed: {
    padded() {
      const width = this.width - this.margin.left - this.margin.right;
      const height = this.height - this.margin.top - this.margin.bottom;
      return { width, height };
    },
    formattedData()   {
      let formattedData = cloneDeep(this.data);
      formattedData.forEach(d => {
        d.date = this.parseTime(d.date);
        this.seriesNames.forEach(function(series) {
          d[series] = +d[series];
        });
      });

      return formattedData;
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  watch: {
    width: function widthChanged() {
      this.initialize();
      this.update();
    }
  },
  methods: {
    onResize() {
      this.width = this.$el.offsetWidth;
      this.height = this.$el.offsetWidth / 2;
    },
    createLine: d3.line().x(d => d.x).y(d => d.y),
    parseTime: d3.timeParse("%Y"),
    initialize() {
      this.scale.x = d3.scaleTime().range([0, this.padded.width]);
      this.scale.y = d3.scaleLinear().range([this.padded.height, 0]);
      d3.axisLeft().scale(this.scale.y);
      d3.axisBottom().scale(this.scale.x);
    },
    update() {
      const data = this.formattedData,
        points = this.points,
        scale = this.scale,
        lines = this.lines,
        createLine = this.createLine,
        colorScale = this.colorScale;

      let maxes = [],
        mins = [];
      this.seriesNames.forEach(function(series) {
        const max = d3.max(data, d => d[series]),
          min = d3.min(data, d => d[series]);
        maxes.push(max);
        mins.push(min);
      });
      this.scale.x.domain(d3.extent(data, d => d.date));
      this.scale.y.domain([d3.min(mins), d3.max(maxes)]);

      this.seriesNames.forEach(function(series, i) {
        points[i] = [];
        lines[i] = {};
        for (const d of data) {
          points[i].push({
            x: scale.x(d.date),
            y: scale.y(d[series]),
          });
        }
        lines[i].line = createLine(points[i]);
        lines[i].color = colorScale(series);
      });

      d3.select('#reports-of-problems-uk__x-axis')
        .call(d3.axisBottom(this.scale.x).tickPadding(10))
        .attr('transform', 'translate(' + this.margin.left + ', ' + (this.margin.top + this.padded.height) + ')');
      d3.select('#reports-of-problems-uk__y-axis')
        .call(d3.axisLeft(this.scale.y)
        .tickValues([0, 5e3, 10e3, 15e3, 19559]))
        .attr('transform', 'translate(' + this.margin.left + ', ' + this.margin.top  + ')')
        .selectAll(".tick line").attr("x2", this.padded.width);
    }
  }
}
</script>

<style scoped lang="scss">
  path {
    fill: none;
    stroke-width: 3px;
  }
</style>
